/**
 * Shared WebSocket hook providing auto-reconnect, ping-pong keepalive,
 * and visibility-change reconnect logic.
 *
 * Both usePoolBet and useComments delegate all WS plumbing here. Message
 * parsing and state updates remain in the calling hook via `onMessage`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { backoffDelay, buildWebSocketUrl } from '@/lib/wsUtils';

export interface UseWebSocketOptions {
  onMessage: (message: unknown) => void;
  /** Called once the connection opens. Use for clearing error state, etc. */
  onOpen?: () => void;
  /** Called when the socket errors. Use for setting error state. */
  onError?: () => void;
  /**
   * Close codes that suppress automatic reconnection.
   * Default: [4001] (unauthenticated — reconnecting would loop forever).
   */
  stopReconnectCodes?: number[];
}

export function useWebSocket(
  path: string,
  options: UseWebSocketOptions
): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Keep callbacks and config in refs so `connect` never captures a stale closure.
  const onMessageRef = useRef(options.onMessage);
  const onOpenRef = useRef(options.onOpen);
  const onErrorRef = useRef(options.onError);
  const stopCodesRef = useRef(options.stopReconnectCodes ?? [4001]);
  onMessageRef.current = options.onMessage;
  onOpenRef.current = options.onOpen;
  onErrorRef.current = options.onError;
  stopCodesRef.current = options.stopReconnectCodes ?? [4001];

  const connect = useCallback(() => {
    // Guard against OPEN, CONNECTING, and CLOSING states — a new socket should
    // only be created once the previous one has fully closed.
    const state = wsRef.current?.readyState;
    if (
      state === WebSocket.OPEN ||
      state === WebSocket.CONNECTING ||
      state === WebSocket.CLOSING
    ) {
      return;
    }

    const ws = new WebSocket(buildWebSocketUrl(path));
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setIsConnected(true);
      onOpenRef.current?.();
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as { type?: string };
        if (msg.type === 'pong') return;
        onMessageRef.current(msg);
      } catch (err) {
        console.error('WS message parse error:', err);
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (stopCodesRef.current.includes(event.code)) return;
      const delay = backoffDelay(reconnectAttemptsRef.current);
      reconnectAttemptsRef.current += 1;
      reconnectTimeoutRef.current = window.setTimeout(connect, delay);
    };

    ws.onerror = () => {
      console.error('WebSocket error');
      onErrorRef.current?.();
    };
  }, [path]);

  const cleanupWs = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      // Null out onclose before closing so the reconnect loop doesn't fire
      // on intentional teardown (unmount or path change).
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  useEffect(() => {
    connect();
    return cleanupWs;
  }, [connect, cleanupWs]);

  // When a forgotten/background tab becomes visible again the browser may have
  // throttled ping timers or killed the socket. Reconnect immediately so the
  // user gets fresh state without waiting for the next backoff tick.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const state = wsRef.current?.readyState;
        if (state === WebSocket.CLOSED || state === undefined) {
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          reconnectAttemptsRef.current = 0;
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect]);

  return { isConnected };
}
